/**
 * Project Context - Manages project-related state across the application
 * Provides centralized state management for projects, loading states, and error handling
 */

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import projectService from '../services/projectService';
import projectEnhancementService from '../services/projectEnhancementService';

// Initial state
const initialState = {
  projects: [],
  selectedProject: null,
  projectData: null,
  loading: false,
  error: null,
  lastUpdated: null
};

// Action types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_PROJECTS: 'SET_PROJECTS',
  SET_SELECTED_PROJECT: 'SET_SELECTED_PROJECT',
  SET_PROJECT_DATA: 'SET_PROJECT_DATA',
  CLEAR_ERROR: 'CLEAR_ERROR',
  CLEAR_SELECTED_PROJECT: 'CLEAR_SELECTED_PROJECT',
  UPDATE_LAST_VIEWED: 'UPDATE_LAST_VIEWED'
};

// Reducer function
const projectReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
        error: action.payload ? null : state.error
      };
    
    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    
    case ActionTypes.SET_PROJECTS:
      return {
        ...state,
        projects: action.payload,
        loading: false,
        error: null,
        lastUpdated: new Date()
      };
    
    case ActionTypes.SET_SELECTED_PROJECT:
      return {
        ...state,
        selectedProject: action.payload,
        loading: false,
        error: null
      };
    
    case ActionTypes.SET_PROJECT_DATA:
      return {
        ...state,
        projectData: action.payload,
        loading: false,
        error: null
      };
    
    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    case ActionTypes.CLEAR_SELECTED_PROJECT:
      return {
        ...state,
        selectedProject: null,
        projectData: null
      };
    
    case ActionTypes.UPDATE_LAST_VIEWED:
      return {
        ...state,
        projects: state.projects.map(project =>
          project.Id === action.payload.projectId
            ? { ...project, LastViewed: new Date() }
            : project
        ),
        selectedProject: state.selectedProject?.Id === action.payload.projectId
          ? { ...state.selectedProject, LastViewed: new Date() }
          : state.selectedProject
      };
    
    default:
      return state;
  }
};

// Create context
const ProjectContext = createContext();

// Provider component
export const ProjectProvider = ({ children }) => {
  const [state, dispatch] = useReducer(projectReducer, initialState);

  // Action creators
  const setLoading = useCallback((loading) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: error });
  }, []);

  const setProjects = useCallback((projects) => {
    dispatch({ type: ActionTypes.SET_PROJECTS, payload: projects });
  }, []);

  const setSelectedProject = useCallback((project) => {
    dispatch({ type: ActionTypes.SET_SELECTED_PROJECT, payload: project });
  }, []);

  const setProjectData = useCallback((data) => {
    dispatch({ type: ActionTypes.SET_PROJECT_DATA, payload: data });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  }, []);

  const clearSelectedProject = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_SELECTED_PROJECT });
  }, []);

  const updateLastViewed = useCallback((projectId) => {
    dispatch({ type: ActionTypes.UPDATE_LAST_VIEWED, payload: { projectId } });
  }, []);

  // Service methods
  // `background: true` revalidates WITHOUT flipping `loading`, so whatever is
  // already on screen stays rendered while the request is in flight. Callers use
  // it for "refresh what the user is already looking at" — flipping loading
  // there swaps the grid for a spinner and rebuilds it from scratch, which is
  // what made returning to /projects look like a full page reload rather than a
  // navigation. With no cached projects there is nothing to preserve, so it
  // falls back to the normal loading state.
  const loadProjects = useCallback(async (forceRefresh = false, { background = false } = {}) => {
    try {
      // If forcing refresh, always reload from API to get latest filtered data
      // This ensures we get projects filtered by user + UnrealProjects secret
      if (forceRefresh) {
        console.log('🔄 Force refreshing projects from API (filtered list)...');
        if (!background || state.projects.length === 0) {
          setLoading(true);
        }
      } else if (state.projects.length > 0) {
        // If we have cached data and not forcing refresh, use it
        console.log('✅ Using existing projects from context state');
        return; // Don't reload if we already have data
      } else {
        // No cached data, load from API
        setLoading(true);
      }
      
      // Get user email from session storage for filtering
      let userEmail = null;
      try {
        const storedEmail = sessionStorage.getItem('userEmail');
        console.log('🔍 DEBUG: Raw stored email value:', storedEmail);
        console.log('🔍 DEBUG: Session storage keys:', Object.keys(sessionStorage));
        console.log('🔍 DEBUG: All session storage:', { ...sessionStorage });
        
        if (storedEmail) {
          userEmail = storedEmail;
          console.log('📧 Using user email for project filtering:', userEmail);
        } else {
          console.log('⚠️ No user email found, showing all projects');
          console.log('🔍 DEBUG: Available session storage items:');
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            console.log(`  - ${key}: ${sessionStorage.getItem(key)}`);
          }
        }
      } catch (error) {
        console.warn('Could not get user email from session storage:', error);
      }
      
      const projects = await projectService.getAllProjects(forceRefresh, userEmail);
      
      // Enhance projects with thumbnails from localStorage
      const enhancedProjects = projectEnhancementService.enhanceProjectsWithThumbnails(projects);
      
      setProjects(enhancedProjects);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setProjects, setError, state.projects.length]);

  // Check if projects are already loaded
  const areProjectsLoaded = useCallback(() => {
    return state.projects.length > 0;
  }, [state.projects.length]);

  const loadProjectByCode = useCallback(async (projectCode, forceRefresh = false) => {
    try {
      setLoading(true);
      const project = await projectService.getProjectByCode(projectCode, forceRefresh);
      setSelectedProject(project);
      
      // Also load project data
      try {
        const projectData = await projectService.getProjectData(projectCode, forceRefresh);
        setProjectData(projectData);
      } catch (dataError) {
        console.warn('Could not load project data:', dataError.message);
        // Don't fail the whole operation if project data fails
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setSelectedProject, setProjectData, setError]);

  const searchProjects = useCallback(async (query) => {
    try {
      setLoading(true);
      const results = await projectService.searchProjects(query);
      return results;
    } catch (error) {
      setError(error.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError]);

  // Utility methods
  const isProjectExpired = useCallback((project) => {
    return projectService.isProjectExpired(project);
  }, []);

  const canViewProject = useCallback((project) => {
    return projectService.canViewProject(project);
  }, []);

  const canAccessProjectFiles = useCallback((project) => {
    return projectService.canAccessProjectFiles(project);
  }, []);

  const formatFileSize = useCallback((bytes) => {
    return projectService.formatFileSize(bytes);
  }, []);

  const formatDate = useCallback((date) => {
    return projectService.formatDate(date);
  }, []);

  // Cache management methods
  const clearCache = useCallback(() => {
    projectService.clearCache();
  }, []);

  const clearProjectCache = useCallback((projectCode) => {
    projectService.clearProjectCache(projectCode);
  }, []);

  const preloadAllProjects = useCallback(async () => {
    try {
      setLoading(true);
      const projects = await projectService.preloadAllProjects();
      setProjects(projects);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setProjects, setError]);

  const preloadProject = useCallback(async (projectCode) => {
    try {
      setLoading(true);
      const project = await projectService.preloadProject(projectCode);
      setSelectedProject(project);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [setLoading, setSelectedProject, setError]);

  const getCacheStats = useCallback(() => {
    return projectService.getCacheStats();
  }, []);

  const isProjectsCached = useCallback(() => {
    return projectService.isProjectsCached();
  }, []);

  const isProjectCached = useCallback((projectCode) => {
    return projectService.isProjectCached(projectCode);
  }, []);

  // Data change handling methods
  const handleDataChange = useCallback((action, projectCode = null) => {
    projectService.handleDataChange(action, projectCode);
  }, []);

  const isDataFresh = useCallback((projectCode = null) => {
    return projectService.isDataFresh(projectCode);
  }, []);

  const getDataAge = useCallback((projectCode = null) => {
    return projectService.getDataAge(projectCode);
  }, []);

  // Context value
  const value = {
    // State
    ...state,
    
    // Actions
    setLoading,
    setError,
    setProjects,
    setSelectedProject,
    setProjectData,
    clearError,
    clearSelectedProject,
    updateLastViewed,
    
    // Service methods
    loadProjects,
    loadProjectByCode,
    searchProjects,
    areProjectsLoaded,
    
    // Utility methods
    isProjectExpired,
    canViewProject,
    canAccessProjectFiles,
    formatFileSize,
    formatDate,
    
    // Cache management methods
    clearCache,
    clearProjectCache,
    preloadAllProjects,
    preloadProject,
    getCacheStats,
    isProjectsCached,
    isProjectCached,
    
    // Data change handling methods
    handleDataChange,
    isDataFresh,
    getDataAge
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

// Custom hook to use the project context
export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export default ProjectContext;
